import { Center, Divider, Modal, Stack } from '@collinsonx/design-system/core';
import { useDisclosure } from '@collinsonx/design-system/hooks';
import { ProductCategory } from '@collinsonx/utils/generatedTypes/graphql';
import useLocale from 'hooks/useLocale';
import useNavigation from 'hooks/useNavigation';
import useProduct from 'hooks/useProduct';
import { FC } from 'react';
import colors from 'ui/colour-constants';

import type { ComponentProps, FastTrackNoAvailabilityCopy } from './props';

import {
  CapacityErrorMessage,
  Footer,
  NoFlightInfoErrorMessage,
  LoungeClosedMessage,
  MaxPeriodMessage,
  MinPeriodMessage,
  ServerErrorMessage,
} from './components';
import { FooterStyle, HeaderStyle } from './enums';
import { setHeaderTitle } from './helpers';

export function internalServerError() {
  return generateError(
    HeaderStyle.SERVER,
    FooterStyle.INTERNAL_SERVER_ERROR,
    <ServerErrorMessage />
  );
}

export function capacityDefaultError(
  fastTrackNoAvailabilityCopy?: FastTrackNoAvailabilityCopy
) {
  return generateError(
    HeaderStyle.CAPACITY,
    FooterStyle.CAPACITY,
    <CapacityErrorMessage
      descriptionOverride={fastTrackNoAvailabilityCopy?.description}
    />,
    fastTrackNoAvailabilityCopy
  );
}

export function loungeClosedError() {
  return generateError(
    HeaderStyle.LOUNGE_CLOSED,
    FooterStyle.LOUNGE_CLOSED,
    <LoungeClosedMessage />
  );
}

export function maxPeriodError(days: number, category?: ProductCategory) {
  return generateError(
    HeaderStyle.MAX_PERIOD,
    FooterStyle.MAX_PERIOD,
    <MaxPeriodMessage category={category} days={days} />
  );
}

export function minPeriodError(hours: number) {
  return generateError(
    HeaderStyle.MIN_PERIOD,
    FooterStyle.MIN_PERIOD,
    <MinPeriodMessage hours={hours} />
  );
}

export function flightInfoNotFoundError() {
  return generateError(
    HeaderStyle.NO_FLIGHT,
    FooterStyle.FLIGHT_INFO_NOT_FOUND,
    <NoFlightInfoErrorMessage />
  );
}

function generateError(
  header: HeaderStyle,
  footer: FooterStyle,
  Message: JSX.Element,
  capacityCopy?: FastTrackNoAvailabilityCopy
) {
  return (
    <AvailableSlotsModal
      capacityCopy={capacityCopy}
      footerStyle={footer}
      headerStyle={header}
      Message={Message}
    />
  );
}

const AvailableSlotsModal: FC<ComponentProps> = ({
  capacityCopy,
  footerStyle,
  headerStyle,
  Message,
}) => {
  const [opened, { close }] = useDisclosure(true);
  const translations = useLocale();
  const { navigateToLounge } = useNavigation();
  const { isFastTrackEnabled } = useProduct();

  const capacityTitleOverride =
    headerStyle === HeaderStyle.CAPACITY
      ? capacityCopy?.title ??
        (isFastTrackEnabled
          ? translations.fastTrack.errors.noAvailability.title
          : translations.lounge.errors.capacity.title)
      : undefined;

  const capacityCtaResolved =
    footerStyle === FooterStyle.CAPACITY
      ? capacityCopy?.cta ??
        (isFastTrackEnabled
          ? translations.fastTrack.errors.noAvailability.cta
          : undefined)
      : undefined;

  const handleCloseCapacityErrorModal = () => {
    if (footerStyle === FooterStyle.CAPACITY) {
      navigateToLounge();
    } else {
      close();
    }
  };

  return (
    <Modal.Root onClose={handleCloseCapacityErrorModal} opened={opened}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fw={800} fz="lg">
            {setHeaderTitle(
              headerStyle,
              translations.lounge.errors,
              capacityTitleOverride
            )}
          </Modal.Title>
          <Modal.CloseButton ml={0} />
        </Modal.Header>
        <Modal.Body p={0}>
          <Center>
            <Stack gap={0}>
              <Stack style={{ padding: '1rem' }}>{Message}</Stack>
              <Divider m={0} my="sm" style={{ marginBottom: '0px' }} />
              <Stack bg={colors.dialogFooter} p="lg">
                <Footer
                  capacityCtaOverride={capacityCtaResolved}
                  close={close}
                  footerStyle={footerStyle}
                />
              </Stack>
            </Stack>
          </Center>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
